import { useCallback, useEffect, useState } from 'react';
import { isCompleetVoorFactuur, watOntbreektVoorFactuur, type Partij } from '@qc/core';
import { supabase } from '../supabase.js';

/**
 * Onze eigen bedrijfsgegevens.
 *
 * Ze stonden hardcoded in de afrekening met een KvK-nummer van 00000000 en een
 * testIBAN. Nu staan ze in de database en zijn ze door de admin te wijzigen,
 * want ze komen op elke bon, elke factuur en in elk SEPA-bestand terecht.
 */
export interface Bedrijfsgegevens {
  naam: string;
  kvkNummer: string;
  btwNummer: string;
  iban: string;
  adres: string;
  postcode: string;
  plaats: string;
  email: string;
  telefoon: string;
}

export const LEEG: Bedrijfsgegevens = {
  naam: '',
  kvkNummer: '',
  btwNummer: '',
  iban: '',
  adres: '',
  postcode: '',
  plaats: '',
  email: '',
  telefoon: '',
};

/** Wat er op een factuur van moet worden gemaakt. */
export function alsPartij(gegevens: Bedrijfsgegevens): Partij {
  const adres = [gegevens.adres, [gegevens.postcode, gegevens.plaats].filter(Boolean).join(' ')]
    .filter((deel) => deel.trim() !== '')
    .join(', ');

  return {
    naam: gegevens.naam,
    kvkNummer: gegevens.kvkNummer === '' ? null : gegevens.kvkNummer,
    btwNummer: gegevens.btwNummer === '' ? null : gegevens.btwNummer,
    iban: gegevens.iban === '' ? null : gegevens.iban,
    adres: adres === '' ? null : adres,
  };
}

export interface Bedrijfsgegevensbeeld {
  gegevens: Bedrijfsgegevens;
  laden: boolean;
  bezig: boolean;
  fout: string | null;
  bewaard: boolean;
  ontbreekt: string[];
  compleet: boolean;
  zet: (wijziging: Partial<Bedrijfsgegevens>) => void;
  bewaren: () => Promise<void>;
}

export function useBedrijfsgegevens(): Bedrijfsgegevensbeeld {
  const [gegevens, setGegevens] = useState<Bedrijfsgegevens>(LEEG);
  const [laden, setLaden] = useState(true);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [bewaard, setBewaard] = useState(false);

  useEffect(() => {
    let actueel = true;
    void (async () => {
      const { data, error } = await supabase.from('bedrijfsgegevens').select('*').maybeSingle();
      if (!actueel) return;
      if (error !== null) {
        setFout('We konden je bedrijfsgegevens niet ophalen.');
      } else if (data !== null) {
        setGegevens({
          naam: data.naam ?? '',
          kvkNummer: data.kvk_nummer ?? '',
          btwNummer: data.btw_nummer ?? '',
          iban: data.iban ?? '',
          adres: data.adres ?? '',
          postcode: data.postcode ?? '',
          plaats: data.plaats ?? '',
          email: data.email ?? '',
          telefoon: data.telefoon ?? '',
        });
      }
      setLaden(false);
    })();
    return () => {
      actueel = false;
    };
  }, []);

  const zet = useCallback((wijziging: Partial<Bedrijfsgegevens>) => {
    setBewaard(false);
    setGegevens((huidig) => ({ ...huidig, ...wijziging }));
  }, []);

  const bewaren = useCallback(async () => {
    setBezig(true);
    setFout(null);
    const { error } = await supabase
      .from('bedrijfsgegevens')
      .update({
        naam: gegevens.naam,
        kvk_nummer: gegevens.kvkNummer === '' ? null : gegevens.kvkNummer,
        btw_nummer: gegevens.btwNummer === '' ? null : gegevens.btwNummer,
        iban: gegevens.iban === '' ? null : gegevens.iban,
        adres: gegevens.adres === '' ? null : gegevens.adres,
        postcode: gegevens.postcode === '' ? null : gegevens.postcode,
        plaats: gegevens.plaats === '' ? null : gegevens.plaats,
        email: gegevens.email === '' ? null : gegevens.email,
        telefoon: gegevens.telefoon === '' ? null : gegevens.telefoon,
        bijgewerkt_op: new Date().toISOString(),
      })
      .eq('id', true);
    setBezig(false);
    if (error !== null) {
      setFout('Opslaan lukte niet. Alleen een beheerder mag deze gegevens wijzigen.');
      return;
    }
    setBewaard(true);
  }, [gegevens]);

  const partij = alsPartij(gegevens);

  return {
    gegevens,
    laden,
    bezig,
    fout,
    bewaard,
    ontbreekt: watOntbreektVoorFactuur(partij),
    compleet: isCompleetVoorFactuur(partij),
    zet,
    bewaren,
  };
}
